import { Component, ElementRef, ViewChild, inject } from "@angular/core";
import { MatLegacySnackBar as MatSnackBar } from "@angular/material/legacy-snack-bar";
import { TableComponent } from "src/app/table.component";
import { CareplusNewCustomer } from "src/models/careplus-new-customer";
import { StorageService } from "src/services/storage.service";
import { AgencyService } from "src/app/agency/services/agency.service";
import { Helpers } from "src/helpers/helpers";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import * as Papa from "papaparse";
import jwt_decode from "jwt-decode";
import { of } from "rxjs";
import { SelectionModel } from "@angular/cdk/collections";
import { DeleteData } from "src/app/agency/services/delete-data";
import { AlertDialogComponent } from "src/app/alert-dialog/alert-dialog.component";
import { Router } from "@angular/router";
import { CareplusAgencyService } from "src/services/careplus-agency-service";
import { AccountService } from "src/services/account.service";

@Component({
  selector: "app-careplus-add-customers",
  templateUrl: "./careplus-add-customers.component.html",
  styleUrls: ["./careplus-add-customers.component.css"],
})
export class CareplusAddCustomersComponent {
  private _snackBar = inject(MatSnackBar);
  private _storageService = inject(StorageService);
  private _agencyService = inject(AgencyService);
  public _helper = inject(Helpers);
  private _accountService = inject(AccountService);
  isPanelOpen = true;
  newCustomersFile: any;
  newCustomersEle: string;
  showUploadCard: boolean = false;
  isAgency = false;
  isCreatingBatch = false;
  processing = false;
  source: string | undefined = undefined;
  @ViewChild("csvFileInput") csvFileInput: ElementRef;
  @ViewChild("xlsFileInput") xlsFileInput: ElementRef;
  tableData: CareplusNewCustomer[] = [];
  isTableDataLoading = true;
  agencyUserId: string = "";
  xlsFile: any;
  xlsFileName: string = "";

  constructor() {
    const obj = this.getDecodedAccessToken(this._helper.getToken());
    const userType = obj?.type || "";
    if (
      userType.indexOf("LEVEL 0") === 0 ||
      userType.indexOf("LEVEL 1") === 0 ||
      userType.indexOf("LEVEL 2") === 0
    ) {
      this.isAgency = true;
      this._agencyService.getAgency().subscribe((agency) => {
        this._helper.setAgencyId(agency.id);
        this.source = agency.name;
      });
    }
  }

  ngOnInit(): void {
    this._accountService.getAccount().subscribe((account) => {
      this.agencyUserId = account.id;
    });
  }

  getDecodedAccessToken(token: string): any {
    try {
      return jwt_decode(token);
    } catch (Error) {
      return null;
    }
  }

  onnewCustomers(event) {
    const file = event.target.files[0];
    this.newCustomersEle = file.name;
    this.newCustomersFile = file;
  }

  uploadCSV() {
    if (this.newCustomersFile !== undefined) {
      this.processAndUploadCSV(this.newCustomersFile);
    }
  }

  onXlsFile(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    this.xlsFileName = file.name;
    this.xlsFile = file;
  }

  uploadXls() {
    if (this.xlsFile) {
      this.uploadXlsToCloud(this.xlsFile);
    }
  }

  private processAndUploadCSV(file: File) {
    this.processing = true;

    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      transformHeader: (header: string) => {
        const headerMap: Record<string, string> = {
          firstname: "firstName",
          lastname: "lastName",
          emailaddress: "email",
          email: "email",
          phonenumber: "phoneNumber",
          phone: "phoneNumber",
          dateofbirth: "dateOfBirth",
          dob: "dateOfBirth",
          gender: "gender",
          employeeid: "employeeId",
        };
        const key = header.trim().toLowerCase().replace(/\s+/g, "");
        return (
          headerMap[key] ??
          header.trim().charAt(0).toLowerCase() + header.trim().slice(1)
        );
      },
      complete: (result: { data: CareplusNewCustomer[] }) => {
        const requiredFields: (keyof CareplusNewCustomer)[] = [
          "firstName",
          "lastName",
          "phoneNumber",
          "email",
          "dateOfBirth",
          "gender",
        ];

        for (let i = 0; i < result.data.length; i++) {
          const row = result.data[i];
          const missingFields = requiredFields.filter(
            (field) =>
              row[field] === null ||
              row[field] === undefined ||
              row[field]?.toString().trim() === "",
          );

          if (missingFields.length > 0) {
            this.processing = false;
            this._snackBar.open(
              `Row ${i + 1} is missing required fields: ${missingFields.join(", ")}. Please fix the CSV and try again.`,
              "CLOSE",
              { duration: 8000 },
            );
            return;
          }
        }

        const processedData = result.data.map((row) => ({
          ...row,
          dateOfBirth: this.parseDateString(row.dateOfBirth),
        }));

        const processedCsv = Papa.unparse(processedData);
        const processedBlob = new Blob([processedCsv], { type: "text/csv" });
        const processedFile = new File([processedBlob], file.name, {
          type: "text/csv",
        });
        this.uploadToCloud(processedFile);
      },
      error: (error) => {
        this.processing = false;
        this._snackBar.open("Failed to process CSV file.", "CLOSE", {
          duration: 5000,
        });
        console.error("CSV parsing error:", error);
      },
    });
  }

  private uploadToCloud(file: File) {
    this.processing = true;

    const d = new Date().getTime().toString();
    const fileName = `${d}-agencyUserId:${this.agencyUserId}`;

    this._storageService
      .getCareplusUploadOnlyToken("renew-carepluscustomer-csv")
      .subscribe((token) => {
        this._storageService
          .uploadFileToAzure(
            `/renew-carepluscustomer-csv/${fileName}.csv${token.sasToken}`,
            file,
            "text/csv",
          )
          .subscribe({
            next: () => {
              this.processing = false;
              this.newCustomersEle = "";
              this.newCustomersFile = undefined;
              if (this.csvFileInput)
                this.csvFileInput.nativeElement.value = null;

              this._snackBar.open("The file has been uploaded.", "CLOSE", {
                horizontalPosition: "right",
                duration: 4000,
              });
              location.reload();
            },
            error: () => {
              this.processing = false;
              this._snackBar.open("Failed to upload data to cloud.", "CLOSE", {
                duration: 5000,
              });
            },
          });
      });
  }

  public uploadXlsToCloud(file: File) {
    this.processing = true;

    const d = new Date().getTime().toString();
    const fileName = `${d}-agencyUserId:${this.agencyUserId}`;

    this._storageService
      .getCareplusUploadOnlyToken("renew-carepluscustomerwithmember-xls")
      .subscribe((token) => {
        this._storageService
          .uploadFileToAzure(
            `/renew-carepluscustomerwithmember-xls/${fileName}.xls${token.sasToken}`,
            file,
            file.type,
          )
          .subscribe({
            next: () => {
              this.processing = false;
              this.newCustomersEle = "";
              this.newCustomersFile = undefined;
              if (this.csvFileInput)
                this.csvFileInput.nativeElement.value = null;

              this._snackBar.open("The file has been uploaded.", "CLOSE", {
                horizontalPosition: "right",
                duration: 4000,
              });
              location.reload();
            },
            error: () => {
              this.processing = false;
              this._snackBar.open("Failed to upload data to cloud.", "CLOSE", {
                duration: 5000,
              });
            },
          });
      });
  }

  downloadSampleCSV() {
    const link = document.createElement("a");
    link.href = "assets/files/careplus_template.csv";
    link.download = "careplus_template.csv";
    link.click();
  }

  downloadSampleXLS() {
    const link = document.createElement("a");
    link.href = "assets/files/xls_template.xlsx";
    link.download = "xls_template.xlsx";
    link.click();
  }

  private parseDateString(dateStr: string | undefined): string {
    if (!dateStr) return "";

    const dateString = dateStr.toString().trim();
    if (!dateString) return "";

    const parts = dateString.split(/[-\/]/);
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);

      if (isNaN(day) || isNaN(month) || isNaN(year)) {
        return dateString;
      }
      const paddedDay = day.toString().padStart(2, "0");
      const paddedMonth = month.toString().padStart(2, "0");

      return `${paddedDay}-${paddedMonth}-${year}`;
    }

    return dateString;
  }

  onTableData(data: CareplusNewCustomer[]) {
    this.isTableDataLoading = false;
    this.tableData = data;
    this.isPanelOpen = data.length === 0;
  }

  toggleUploadCard() {
    this.showUploadCard = true;
  }
}
